import { ReactNode, useEffect, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { useProps } from "contexts/PropsContext"

import { Translate } from "../../helpers/token.helpers"

/**
 * Confirmation shell for every $TOKEN admin mutation.
 *
 * 🔴 THE REASON FIELD IS THE CONTROL, NOT A COURTESY. Business B3 declined admin
 * 2FA for V1 on the strength of "role gate + mandatory reason + audit log". Those
 * three things are the COMPLETE protection on operations that move money, so this
 * box is load-bearing and must not become optional or pre-filled with a default.
 *
 * The submit block on an empty reason exists only to avoid a round trip that is
 * guaranteed to fail — the actual enforcement is a `REASON_REQUIRED` rejection in
 * token-service plus a CHECK constraint in its database. Do not treat the client
 * check as the guarantee.
 */
export const ReasonModal = ({
  open,
  title,
  danger,
  /** Defaults to the localized "Confirm" when the caller has nothing sharper. */
  confirmLabel,
  description,
  children,
  submitting,
  error,
  canSubmit = true,
  onCancel,
  onConfirm
}: {
  open: boolean
  title: string
  /** Red styling for the three insider-abuse-sensitive operations. */
  danger?: boolean
  confirmLabel?: string
  description?: ReactNode
  /** Operation-specific fields. */
  children?: ReactNode
  submitting?: boolean
  error?: string
  /** Operation-specific validity, ANDed with a non-blank reason. */
  canSubmit?: boolean
  onCancel: () => void
  onConfirm: (reason: string) => void
}) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { darkMode } = useProps()
  const [reason, setReason] = useState("")

  // Clearing on open, not on close, so a failed submit keeps what was typed —
  // retyping a justification after a PURCHASE_FINAL rejection is pure friction.
  useEffect(() => {
    if (open) setReason("")
  }, [open])

  const reasonOk = reason.trim().length > 0
  const disabled = !reasonOk || !canSubmit || !!submitting

  return (
    <Modal open={open} handler={onCancel}>
      <div className={clsx("w-[520px] max-w-[92vw] rounded-2xl p-6", darkMode ? "bg-[#0C0C1A] text-white" : "bg-white text-gray-900")}>
        <h3 className={clsx("text-xl font-semibold", danger && "text-red-400")}>{title}</h3>

        {description && <div className={clsx("mt-3 text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>{description}</div>}

        {children && <div className="mt-5 grid gap-4">{children}</div>}

        <div className="mt-5">
          <label className="mb-1 block text-sm font-medium">
            {t("token.reasonLabel")} <span className="text-red-400">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            rows={3}
            placeholder={t("token.reasonPlaceholder")}
            className={clsx(
              "w-full rounded-lg border px-3 py-2 text-sm outline-none",
              darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#060715] text-white" : "border-gray-300 bg-white"
            )}
          />
          <p className={clsx("mt-1 text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>
            {t("token.reasonHint")}
          </p>
        </div>

        {error && <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={clsx("rounded-lg px-5 py-2 text-sm transition-all", darkMode ? "bg-[rgba(255,255,255,0.06)] text-white" : "bg-gray-100 text-gray-700", "disabled:opacity-50")}
          >
            {t("token.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={disabled}
            className={clsx(
              "rounded-lg px-5 py-2 text-sm text-white transition-all disabled:cursor-not-allowed disabled:opacity-40",
              danger ? "bg-red-600 hover:bg-red-500" : "bg-[rgba(132,0,168,1)] hover:bg-[rgba(169,78,206,1)]"
            )}
          >
            {submitting ? t("token.working") : confirmLabel ?? t("token.confirm")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
